const formRegistry = {}

function getAllRefs(componentInstance, collectedRefs = {}) {
  if (!componentInstance || !componentInstance.$refs) return collectedRefs

  for (const [refName, refValue] of Object.entries(componentInstance.$refs)) {
    let index = '__' + (Object.keys(collectedRefs).length + 1)
    if (Array.isArray(refValue)) {
      // If the ref is an array (e.g., from v-for), iterate through each item
      collectedRefs[refName + index] = refValue
      refValue.forEach((childComponent) => {
        getAllRefs(childComponent, collectedRefs)
      })
    }
    else {
      // Single ref
      collectedRefs[refName + index] = refValue
      getAllRefs(refValue, collectedRefs)
    }
  }

  return collectedRefs
}

// Register a submit button with a form ID
function registerSubmitButton(formId, el, containerComponent) {
  formRegistry[formId] = {
    instance: containerComponent,
    submitButtons: [],
    listeners: new Map(),
  }

  // Add the element if it's not already in the array
  formRegistry[formId].submitButtons.push(el)

  // Create a reusable listener function we can reference for cleanup
  const validationHandler = (event) => {
    console.log('v-submit validation handler triggered for formId:', formId)
    // Run validation and prevent default click if validation fails
    const isValid = validateForm(formId)
    console.log('Validation result for formId:', formId, 'isValid:', isValid)
    if (!isValid) {
      console.log('Preventing default click for formId:', formId)
      // Prevent the click event from reaching the actual button handler
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
  }

  // Save the listener function for cleanup
  formRegistry[formId].listeners.set(el, validationHandler)

  // Add event listener with capture phase to run before the actual click
  el.addEventListener('click', validationHandler, { capture: true })
}

// Function to scroll to first error element, optionally scoped to a specific container
function scrollToFirstError(scopeContainer = null) {
  // Wait for validation messages to appear
  setTimeout(() => {
    // Ensure scopeContainer is a valid Element node (not a text node or other node type)
    // In Vue 3, $el can sometimes be a text node when using fragments
    if (scopeContainer && scopeContainer.nodeType !== 1) {
      // If it's not an element node, try to get the parent element or fall back to global search
      scopeContainer = scopeContainer.parentElement || null
    }

    console.log('AM3FormValidation: Looking for error elements...', scopeContainer ? 'within scope' : 'globally')

    // Look for AM3 component error indicators and other common patterns
    const errorSelectors = [
      '.am3-input-error', // AM3InputError component
      '.am3-input-wrapper.error', // AM3Input wrapper with error class
      '.error', // Generic error class
      '.text-danger', // Danger text
      '.text-red-500', // Tailwind red text
      '.text-red-600',
      '.border-danger',
      '[class*="error"]', // Any class containing "error"
      'input:invalid', // HTML5 invalid inputs
      '.validation-error', // Custom validation error class
    ]

    let firstErrorElement = null

    // Try each selector until we find an error element
    for (const selector of errorSelectors) {
      let elements
      if (scopeContainer) {
        // Search only within the specified container
        elements = scopeContainer.querySelectorAll(selector)
      }
      else {
        // Search globally
        elements = document.querySelectorAll(selector)
      }

      console.log(`AM3FormValidation: Found ${elements.length} elements for selector: ${selector}`)
      if (elements.length > 0) {
        firstErrorElement = elements[0]
        console.log('AM3FormValidation: First error element found:', firstErrorElement)
        break
      }
    }

    if (firstErrorElement) {
      console.log('AM3FormValidation: Scrolling to error element:', firstErrorElement)

      // If it's an error message, scroll to its parent input container
      if (firstErrorElement.classList.contains('am3-input-error')) {
        const inputContainer = firstErrorElement.closest('.am3-input-container')
        if (inputContainer) {
          firstErrorElement = inputContainer
        }
      }

      // Scroll to the error element with smooth behavior and center it
      firstErrorElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      })

      // Find and focus the actual input within the container
      const inputElement = firstErrorElement.querySelector('input, textarea, select')
      if (inputElement) {
        setTimeout(() => {
          console.log('AM3FormValidation: Focusing input:', inputElement)
          inputElement.focus()
        }, 500)
      }
    }
    else {
      console.log('AM3FormValidation: No error elements found', scopeContainer ? 'within scope' : 'globally')
    }
  }, 200) // Shorter timeout for individual sections
}

// Validate all inputs in a form
function validateForm(formId) {
  console.log('validateForm called for formId:', formId)
  const form = formRegistry[formId]
  if (!form) {
    console.log('No form found for formId:', formId)
    return true
  }
  console.log('Form found:', form)
  let isValid = true
  let inputs = Object.values(getAllRefs(form.instance)).flatMap(x => x)
  console.log('Found inputs for validation:', inputs.length, inputs)

  // Trigger validation on all registered inputs
  for (const vueInstance of inputs) {
    try {
      if (vueInstance) {
        console.log('Validating input:', vueInstance)
        let validationResult = true

        // Check if this is a Vue component instance (has $ properties) or a DOM element
        if (vueInstance.$ || typeof vueInstance.runValidation === 'function' || typeof vueInstance.validate === 'function') {
          // This is a Vue component - try validation methods
          if (typeof vueInstance.runValidation === 'function') {
            validationResult = vueInstance.runValidation()
            console.log('runValidation result:', validationResult)
          }
          else if (typeof vueInstance.validate === 'function') {
            validationResult = vueInstance.validate()
            console.log('validate result:', validationResult)
          }
          else {
            console.log('Vue component but no validation method found on:', vueInstance)
          }
        }
        else if (vueInstance.tagName && vueInstance.nodeType === 1) {
          // This is a raw DOM element - check HTML5 validation
          console.log('Raw DOM element found, checking HTML5 validation')

          if (typeof vueInstance.checkValidity === 'function') {
            validationResult = vueInstance.checkValidity()
            console.log('HTML5 checkValidity result:', validationResult)

            // If invalid, trigger validation UI
            if (!validationResult && typeof vueInstance.reportValidity === 'function') {
              vueInstance.reportValidity()
            }
          }
          else {
            console.log('DOM element has no checkValidity method, skipping validation')
            continue // Skip this element, don't count it as valid or invalid
          }
        }
        else {
          console.log('Unknown input type, skipping:', vueInstance)
          continue // Skip unknown types
        }

        if (validationResult !== true) {
          console.log('Input failed validation:', vueInstance, 'result:', validationResult)
          isValid = false
        }
        else {
          console.log('Input passed validation:', vueInstance)
        }
      }
    }
    catch (err) {
      console.error('Error validating input:', err)
    }
  }

  // If validation failed, scroll to first error within the form's scope
  if (!isValid) {
    // Get the container element for this form to scope the error search
    const formContainer = form.instance?.$el || form.instance?.$refs?.root || null
    scrollToFirstError(formContainer)
  }

  return isValid
}

// Directive for submit buttons
export const vSubmit = {
  mounted(el, binding) {
    const formId = binding.value
    console.log('v-submit directive mounted for formId:', formId, 'on element:', el)
    if (typeof formId !== 'string') {
      console.warn('v-submit directive requires a string ID value')
      return
    }
    registerSubmitButton(formId, el, binding.instance)
  },
  unmounted(el, binding) {
    const formId = binding.value
    if (typeof formId !== 'string') return

    // Remove event listeners and element from registry
    if (formRegistry[formId]) {
      const handler = formRegistry[formId].listeners.get(el)
      if (handler) {
        el.removeEventListener('click', handler, { capture: true })
        formRegistry[formId].listeners.delete(el)
      }
    }
  },
}
